import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, IAppConfig } from '@tcc-mono/shared/app-config';
import { BaseService, Response } from '@tcc-mono/shared/data-access';
import { Animal, TransportGroupAnimalDetails } from '@tcc-mono/vandijk/shared/data-access/models';
import { map, Observable } from 'rxjs';

export class InvoiceStatisticAnimal {
  id: number;
  country_code: string;
  identification_number: string;
  work_number: string;
  purchase_order_id: number;
  purchase_order_number: string;
  transport_group_id: number;
  transport_group_destination_stable_relation: string;
  transport_group_destination_stable: string;
  purchase_invoice_id: number;
  purchase_invoice_number: string;
  sales_invoice_line_id: number;
  sales_invoice_id: number;
  sales_invoice_number: string;
  deceased: boolean;
  do_not_invoice: boolean;
  is_project_animal: boolean;
}

export interface AnimalDetailsRequest {
  country_code: string;
  identification_number: string;
}

export interface AnimalDetailsResponse {
  rvo_animal?: {
    dierLandcode: string | null;
    dierLevensnummer: string | null;
    dierWerknummer: string | null;
    dierSoort: string | null;
    dierCategorie: string | null;
    geboorteDatum: string | null;
    importDatum: string | null;
    dierHaarkleur: string | null;
    dierGeslacht: string | null;
    dierOorspronkelijkeIdentificatie: string | null;
    dierHerkomstLandcode: string | null;
    dierOorsprongLandcode: string | null;
    dierPremiestatus: string | null;
    dierEinddatum: string | null;
    dierRedenEinde: string | null;
    moederLandcode: string | null;
    moederLevensnummer: string | null;
    moederWerknummer: string | null;
    moederGeboorteDatum: string | null;
    moederGeslacht: string | null;
    moederHaarkleur: string | null;
    brondatabase: string | null;
    dierAliasLandcode: string | null;
    dierAliasLevensnummer: string | null;
    dierAliasWerknummer: string | null;
    dierTijdelijkLandcode: string | null;
    dierTijdelijkLevensnummer: string | null;
    dierTijdelijkWerknummer: string | null;
    dierVervangenLandcode: string | null;
    dierVervangenLevensnummer: string | null;
    dierVervangenWerknummer: string | null;
    datumLaatsteMutatieDierkenmerken: string | null;
    verblijfPlaatsen?: Array<{
      meldingeenheid: string | null;
      typeBedrijfsvestiging: string | null;
      aanvoerDatumME: string | null;
      afvoerDatumME: string | null;
      adresOmsME: string | null;
      postcodeOmsME: string | null;
      woonplaatsME: string | null;
      telefoonME: string | null;
      naam: string | null;
    }>;
  } | null;
  rvo_error?: string | null;

  ride_animal?: {
    id: number;
    country_code: string;
    identification_number: string;
    ride_id: number;
    ride_relation: string;
    loading_ubn: string;
    loaded_at: string;
    work_number: string;
    date_of_birth: string;
    gender_code: string;
    gender_name: string;
    color_code: string;
    color_name: string;
    is_healthy: boolean;
    kvs_succes_indicator: string;
    remarks: string | null;
  } | null;
  ride?: {
    id: number;
    destination_ubn: string;
    destination_name: string;
    relation_name: string;
    number: string;
    license_plate: string;
    kvs_ride_id: string;
    kvs_ride_number?: number;
    kvs_ride_error_code?: string | null;
    kvs_ride_error_message?: string | null;
    scanning_done: string;
    closed_at?: string | null;
    animal_count: number;
  } | null;
  purchase_order?: {
    id: number;
    order_number: string;
    relation_name: string;
    total_price: number;
    closed: boolean;
    date?: string | null;
    invoiced?: boolean | number | null;
  } | null;
  animal_purchase_order_line?: {
    id: number;
    weight: number;
    price: number;
    price_ex_vat: number;
    vat: number;
    weight_correct: boolean;
    created_at?: string | null;
    animal: {
      id: number;
      country_code: string;
      identification_number: string;
    };
  } | null;

  animal?: {
    id: number;
    country_code: string;
    identification_number: string;
    work_number: string;
    date_of_birth: string;
    gender_name: string;
    color_name: string;
    is_healthy: boolean;
    rvo_arrival_at?: string | null;
    rvo_departure_at?: string | null;
    created_at?: string | null;
    remarks?: string | null;
  } | null;

  transport_group?: TransportGroupAnimalDetails | null;
}

@Injectable({
  providedIn: 'root'
})
export class AnimalService extends BaseService<Animal> {
  public constructor(
    _http: HttpClient,
    @Inject(APP_CONFIG) appConfig: IAppConfig
  ) {
    super(_http, appConfig);
    this.path = 'api/animals'
  }

  public getInvoiceStatistics(date: string): Observable<InvoiceStatisticAnimal[]> {
    return this.postChild<Response<InvoiceStatisticAnimal[]>>({ date }, 'invoice_statistics').pipe(map(({ data }) => data));
  }

  public getAnimalDetails(request: AnimalDetailsRequest): Observable<AnimalDetailsResponse> {
    return this.postChild<Response<AnimalDetailsResponse>>(request, 'getanimaldetails').pipe(map(({ data }) => data));
  }
}
